import { Controller } from '@hotwired/stimulus'
import * as THREE from 'three'

/**
 * Interactive 3D Picture Frame Controller
 *
 * Creates a realistic 3D picture frame that users can:
 * - Upload images to display OR use provided src URL
 * - Rotate by dragging (mouse or touch)
 * - Zoom with mouse wheel or pinch gesture
 * - Change frame colors
 * - Reset view position
 */
export default class extends Controller {
  static targets = ['container', 'imageUpload']

  static optionalTargets = ['imageUpload']

  static values = {
    frameColor: { type: String, default: 'black' },
    src: String,
  }

  // ===== INITIALIZATION =====
  connect() {
    // remove element and clean up when the modal is closed
    window.addEventListener('ProductDetailsImageModalClosed', () => {
      this.element.remove()
    })

    // auto rotating
    this.autoRotate = true
    this.autoRotateSpeed = 0.003

    this.setupScene()
    this.setupFrameParameters() // Move this BEFORE setupCamera
    this.setupCamera()
    this.setupRenderer()
    this.setupLights()
    this.initializeFrame()
    this.setupEventListeners()
    this.startAnimationLoop()
  }

  /**
   * Initialize the Three.js scene with white background
   */
  setupScene() {
    this.scene = new THREE.Scene()
    this.scene.background = new THREE.Color(0xffffff)
  }

  /**
   * Setup perspective camera with 75° FOV
   * Camera position and aspect ratio based on container dimensions
   */
  setupCamera() {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    if (rect.width === 0 || rect.height === 0) {
      console.error('Container has no dimensions! Check your CSS.')
      // Fallback dimensions
      this.camera = new THREE.PerspectiveCamera(75, 800 / 600, 0.1, 1000)
      this.camera.position.z = 5
      return
    }

    this.camera = new THREE.PerspectiveCamera(75, rect.width / rect.height, 0.1, 1000)
    this.setInitialCameraPosition()
  }

  /**
   * Calculate and set optimal camera position based on container size and frame dimensions
   */
  setInitialCameraPosition() {
    const frameWidth = this.frameWidth || this.defaultFrameWidth
    const frameHeight = this.frameHeight || this.defaultFrameHeight

    if (!frameWidth || !frameHeight) {
      console.error('Frame dimensions still undefined! Setting fallback camera position.')
      this.camera.position.z = 5
      return
    }

    const fov = this.camera.fov * (Math.PI / 180)
    const maxDimension = Math.max(frameWidth, frameHeight)
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.3
    const minDistance = Math.max(distance, 2.5)
    const maxDistance = Math.min(minDistance, 8)

    this.camera.position.z = maxDistance
  }

  /**
   * Setup WebGL renderer with proper color management
   */
  setupRenderer() {
    this.renderer = new THREE.WebGLRenderer({ antialias: true })

    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.renderer.setSize(rect.width, rect.height)
    this.renderer.setPixelRatio(window.devicePixelRatio)
    this.renderer.shadowMap.enabled = false // Shadows disabled for performance

    // Configure color management for proper image display
    this.renderer.outputColorSpace = THREE.SRGBColorSpace
    this.renderer.toneMapping = THREE.NoToneMapping
    this.renderer.toneMappingExposure = 1.0

    container.appendChild(this.renderer.domElement)
  }

  /**
   * Setup lighting optimized for frame and mat only:
   * - Photo uses MeshBasicMaterial (unaffected by lights)
   * - Balanced lighting for frame and mat visibility
   */
  setupLights() {
  // Increased ambient lighting for overall scene brightness
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.7) // Increased from 0.4
    this.scene.add(ambientLight)

    // Main directional light for frame definition
    const frameLight = new THREE.DirectionalLight(0xffffff, 0.8) // Increased from 0.5
    frameLight.position.set(2, 3, 4)
    frameLight.castShadow = false
    this.scene.add(frameLight)

    // Fill light from opposite side for balanced lighting
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.5) // Increased from 0.3
    fillLight.position.set(-2, -1, 3)
    fillLight.castShadow = false
    this.scene.add(fillLight)

    // Top light for mat illumination
    const matLight = new THREE.DirectionalLight(0xffffff, 0.4) // Increased from 0.25
    matLight.position.set(0, 5, 1)
    matLight.castShadow = false
    this.scene.add(matLight)
  }

  /**
   * Initialize frame dimensions, materials, colors, and interaction state
   */
  setupFrameParameters() {
    // Initialize photo texture with proper settings
    this.photoTexture = new THREE.Texture()
    this.photoTexture.colorSpace = THREE.SRGBColorSpace
    this.photoTexture.flipY = true // Default to flipped for proper orientation
    this.photoTexture.premultiplyAlpha = false
    this.photoTexture.format = THREE.RGBAFormat // Required for sRGB color space
    this.photoTexture.type = THREE.UnsignedByteType // Required for sRGB
    this.photoMaterial = null

    // Frame physical dimensions (in Three.js units) - set default aspect ratio
    this.defaultFrameWidth = 3
    this.defaultFrameHeight = 2
    this.frameWidth = this.defaultFrameWidth
    this.frameHeight = this.defaultFrameHeight
    this.frameBorderWidth = 0.1 // Width of frame border
    this.frameDepth = 0.05 // How deep the frame extrudes
    this.matWidth = 0.2 // Width of mat around photo

    // Available frame color options
    this.frameColors = {
      black: 0x2a2a2a, // Slightly lighter black to avoid too much darkness
      white: 0xf8f8f5, // Warm off-white
      natural: 0xd4bf92, // Natural wood tone
    }

    this.currentFrameColor = this.frameColors[this.frameColorValue]

    // State tracking for mouse/touch interactions
    this.imageLoaded = false
    this.isDragging = false
    this.previousMousePosition = { x: 0, y: 0 }

    // Touch-specific state tracking
    this.touches = new Map() // Track multiple touches
    this.isRotating = false
    this.isPinching = false
    this.lastPinchDistance = 0
    this.previousTouchPosition = { x: 0, y: 0 }
  }

  /**
   * Create and add the initial frame to the scene
   */
  initializeFrame() {
    this.frame = this.createFrame()
    this.scene.add(this.frame)

    // Load image from src if provided
    if (this.hasSrcValue && this.srcValue) {
      this.loadImageFromSrc(this.srcValue)
    }
  }

  /**
   * Load image from src URL and update frame
   * @param {string} srcUrl - URL of the image to load
   */
  loadImageFromSrc(srcUrl) {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Handle CORS if needed
    img.onload = () => {
      // Dispose of previous texture
      if (this.photoTexture.image) {
        this.photoTexture.dispose()
      }

      // Create new texture from loaded image with proper settings
      const newTexture = new THREE.Texture(img)
      newTexture.needsUpdate = true
      newTexture.colorSpace = THREE.SRGBColorSpace
      newTexture.flipY = true // Flip Y for proper orientation
      newTexture.premultiplyAlpha = false // Don't premultiply alpha
      newTexture.format = THREE.RGBAFormat // Required for sRGB color space
      newTexture.type = THREE.UnsignedByteType // Required for sRGB

      // Update photo material with new texture
      if (this.photoMaterial) {
        this.photoMaterial.map = newTexture
        this.photoMaterial.needsUpdate = true
      }

      this.photoTexture = newTexture
      this.updateFrameDimensions(img)
    }
    img.onerror = (error) => {
      console.error(`Failed to load image from src: ${srcUrl}`, error)
    }
    img.src = srcUrl
  }

  // ===== FRAME CONSTRUCTION =====

  /**
   * Create the complete picture frame assembly
   * Layers from back to front:
   * 1. Backing (black cardboard at rear)
   * 2. Photo (recessed within frame)
   * 3. Mat (white border around photo)
   * 4. Frame borders (extruded frame structure)
   */
  createFrame() {
    const frameGroup = new THREE.Group()

    // Calculate photo dimensions (frame minus borders and mat)
    const photoWidth = this.frameWidth - 2 * this.frameBorderWidth - 2 * this.matWidth
    const photoHeight = this.frameHeight - 2 * this.frameBorderWidth - 2 * this.matWidth

    // 1. PHOTO - Main image display, recessed within frame
    const photoGeometry = new THREE.PlaneGeometry(photoWidth, photoHeight)
    // Use MeshBasicMaterial for photo - unaffected by lighting, shows true colors
    const photoMaterial = new THREE.MeshBasicMaterial({
      map: this.photoTexture,
      side: THREE.DoubleSide,
    })
    this.photoMaterial = photoMaterial
    const photo = new THREE.Mesh(photoGeometry, photoMaterial)
    photo.position.z = -0.033 // Recessed about 2/3 into frame depth
    frameGroup.add(photo)

    // 2. MAT - White border around photo
    const mat = this.createMat()
    mat.position.z = -0.030 // Slightly in front of photo
    frameGroup.add(mat)

    // 3. FRAME BORDERS - Main structural frame
    this.addFrameBorders(frameGroup)

    // 4. BACKING - Black cardboard backing
    const backing = this.createBacking()
    frameGroup.add(backing)

    return frameGroup
  }

  /**
   * Create the extruded frame borders using shape extrusion
   * Creates a rectangular frame with a rectangular hole in the center
   */
  addFrameBorders(frameGroup) {
    // Outer frame shape
    const outerShape = new THREE.Shape()
    outerShape.moveTo(-this.frameWidth / 2, -this.frameHeight / 2)
    outerShape.lineTo(this.frameWidth / 2, -this.frameHeight / 2)
    outerShape.lineTo(this.frameWidth / 2, this.frameHeight / 2)
    outerShape.lineTo(-this.frameWidth / 2, this.frameHeight / 2)
    outerShape.lineTo(-this.frameWidth / 2, -this.frameHeight / 2)

    // Inner cutout (where photo/mat goes)
    const innerWidth = this.frameWidth - 2 * this.frameBorderWidth
    const innerHeight = this.frameHeight - 2 * this.frameBorderWidth

    const innerShape = new THREE.Shape()
    innerShape.moveTo(-innerWidth / 2, -innerHeight / 2)
    innerShape.lineTo(innerWidth / 2, -innerHeight / 2)
    innerShape.lineTo(innerWidth / 2, innerHeight / 2)
    innerShape.lineTo(-innerWidth / 2, innerHeight / 2)
    innerShape.lineTo(-innerWidth / 2, -innerHeight / 2)

    // Add hole to outer shape
    outerShape.holes.push(innerShape)

    // Extrude the frame shape with beveled edges
    const frameGeometry = new THREE.ExtrudeGeometry(outerShape, {
      depth: this.frameDepth,
      bevelEnabled: true,
      bevelThickness: 0.005,
      bevelSize: 0.005,
      bevelSegments: 3,
      curveSegments: 16,
    })

    // Position frame to extrude backwards (negative Z)
    frameGeometry.translate(0, 0, -this.frameDepth)

    // Frame material with natural appearance
    const frameMaterial = new THREE.MeshStandardMaterial({
      color: this.currentFrameColor,
      roughness: 0.4,
      metalness: 0.0,
    })

    const frameMesh = new THREE.Mesh(frameGeometry, frameMaterial)
    frameMesh.position.z = 0
    frameGroup.add(frameMesh)
  }

  /**
   * Create the white mat that borders the photo
   * Uses thin extrusion with beveled edges for realistic appearance
   */
  createMat() {
    const matOuterWidth = this.frameWidth - 2 * this.frameBorderWidth
    const matOuterHeight = this.frameHeight - 2 * this.frameBorderWidth
    const photoWidth = matOuterWidth - 2 * this.matWidth
    const photoHeight = matOuterHeight - 2 * this.matWidth

    // Outer mat shape
    const matShape = new THREE.Shape()
    matShape.moveTo(-matOuterWidth / 2, -matOuterHeight / 2)
    matShape.lineTo(matOuterWidth / 2, -matOuterHeight / 2)
    matShape.lineTo(matOuterWidth / 2, matOuterHeight / 2)
    matShape.lineTo(-matOuterWidth / 2, matOuterHeight / 2)
    matShape.lineTo(-matOuterWidth / 2, -matOuterHeight / 2)

    // Photo cutout hole
    const hole = new THREE.Path()
    hole.moveTo(-photoWidth / 2, -photoHeight / 2)
    hole.lineTo(photoWidth / 2, -photoHeight / 2)
    hole.lineTo(photoWidth / 2, photoHeight / 2)
    hole.lineTo(-photoWidth / 2, photoHeight / 2)
    hole.lineTo(-photoWidth / 2, -photoHeight / 2)

    matShape.holes.push(hole)

    // Thin extrusion for mat thickness
    const matGeometry = new THREE.ExtrudeGeometry(matShape, {
      depth: 0.008,
      bevelEnabled: true,
      bevelThickness: 0.003,
      bevelSize: 0.003,
      bevelSegments: 3,
      curveSegments: 12,
    })

    matGeometry.computeVertexNormals()

    // White mat material with proper color
    const matMaterial = new THREE.MeshStandardMaterial({
      color: 0xffffff, // Pure white instead of off-white
      roughness: 0.9, // Very rough for matte finish
      metalness: 0.0,
    })

    const mat = new THREE.Mesh(matGeometry, matMaterial)
    mat.position.z = -0.002
    return mat
  }

  /**
   * Create the black backing board that goes behind the photo
   */
  createBacking() {
    const backingGeometry = new THREE.PlaneGeometry(
      this.frameWidth, // Full frame width
      this.frameHeight, // Full frame height
    )

    const backingMaterial = new THREE.MeshStandardMaterial({
      color: 0x000000, // Black
      side: THREE.DoubleSide,
    })

    const backing = new THREE.Mesh(backingGeometry, backingMaterial)
    // Position at the very back of the frame
    backing.position.z = -this.frameDepth - 0.001
    backing.name = 'backing'

    return backing
  }

  // ===== EVENT HANDLING =====

  /**
   * Setup all mouse, touch, and window event listeners
   */
  setupEventListeners() {
    // Mouse interaction for frame rotation
    this.element.addEventListener('mousedown', this.handleMouseDown)
    this.element.addEventListener('mousemove', this.handleMouseMove)
    this.element.addEventListener('mouseup', this.handleMouseUp)

    // Touch interaction for frame rotation and pinch zoom
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: false })
    this.element.addEventListener('touchmove', this.handleTouchMove, { passive: false })
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: false })

    // Mouse wheel for zooming
    this.element.addEventListener('wheel', this.handleWheel, { passive: false })

    // Window resize handling
    window.addEventListener('resize', this.handleResize)

    // Image upload only if target exists
    if (this.hasImageUploadTarget) {
      this.imageUploadTarget.addEventListener('change', this.handleImageUpload.bind(this))
    }
  }

  // ===== MOUSE EVENT HANDLERS =====

  /**
   * Start drag interaction
   */
  handleMouseDown = () => {
    this.isDragging = true
    // Pause auto-rotation during manual interaction
    this.autoRotate = false
  }

  /**
   * Handle frame rotation during drag
   * Rotates frame based on mouse movement delta
   */
  handleMouseMove = (e) => {
    const deltaMove = {
      x: e.clientX - this.previousMousePosition.x,
      y: e.clientY - this.previousMousePosition.y,
    }

    // Rotate frame if dragging
    if (this.isDragging && this.frame) {
      this.frame.rotation.y += deltaMove.x * 0.01 // Horizontal rotation
      this.frame.rotation.x += deltaMove.y * 0.01 // Vertical rotation
    }

    // Track mouse position for next frame
    this.previousMousePosition = {
      x: e.clientX,
      y: e.clientY,
    }
  }

  /**
   * End drag interaction
   */
  handleMouseUp = () => {
    this.isDragging = false

    // Resume auto-rotation after a short delay
    setTimeout(() => {
      if (!this.isDragging) {
        this.autoRotate = true
      }
    }, 2500) // Wait 2.5 seconds before resuming auto-rotation
  }

  // ===== TOUCH EVENT HANDLERS =====

  /**
   * Handle touch start - supports single touch rotation and two-finger pinch zoom
   */
  handleTouchStart = (e) => {
    e.preventDefault() // Prevent scrolling and other default behaviors

    // Clear existing touches and track new ones
    this.touches.clear()

    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      this.touches.set(touch.identifier, {
        x: touch.clientX,
        y: touch.clientY,
        startX: touch.clientX,
        startY: touch.clientY,
      })
    }

    const touchCount = this.touches.size

    if (touchCount === 1) {
      // Single touch - start rotation
      this.isRotating = true
      this.isPinching = false
      this.autoRotate = false // Pause auto-rotation

      const firstTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: firstTouch.x, y: firstTouch.y }
    }
    else if (touchCount === 2) {
      // Two touches - start pinch zoom
      this.isRotating = false
      this.isPinching = true
      this.autoRotate = false

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate initial distance between touches
      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )
    }
  }

  /**
   * Handle touch move - performs rotation or pinch zoom based on touch count
   */
  handleTouchMove = (e) => {
    e.preventDefault() // Prevent scrolling

    // Update touch positions
    for (let i = 0; i < e.touches.length; i++) {
      const touch = e.touches[i]
      if (this.touches.has(touch.identifier)) {
        const storedTouch = this.touches.get(touch.identifier)
        storedTouch.x = touch.clientX
        storedTouch.y = touch.clientY
        this.touches.set(touch.identifier, storedTouch)
      }
    }

    const touchCount = this.touches.size

    if (touchCount === 1 && this.isRotating && this.frame) {
      // Single touch rotation
      const currentTouch = Array.from(this.touches.values())[0]

      const deltaMove = {
        x: currentTouch.x - this.previousTouchPosition.x,
        y: currentTouch.y - this.previousTouchPosition.y,
      }

      // Apply rotation with touch-friendly sensitivity
      this.frame.rotation.y += deltaMove.x * 0.008 // Slightly more sensitive than mouse
      this.frame.rotation.x += deltaMove.y * 0.008

      // Update previous position
      this.previousTouchPosition = { x: currentTouch.x, y: currentTouch.y }
    }
    else if (touchCount === 2 && this.isPinching) {
      // Two-finger pinch zoom
      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      // Calculate current distance between touches
      const currentDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )

      // Calculate zoom delta
      const deltaDistance = currentDistance - this.lastPinchDistance
      const zoomSensitivity = 0.01

      // Apply zoom to camera
      this.camera.position.z -= deltaDistance * zoomSensitivity
      this.camera.position.z = Math.max(2, Math.min(this.camera.position.z, 10))

      // Update last distance for next calculation
      this.lastPinchDistance = currentDistance
    }
  }

  /**
   * Handle touch end - cleanup touch tracking and resume auto-rotation
   */
  handleTouchEnd = (e) => {
    e.preventDefault()

    // Remove ended touches from tracking
    const activeTouchIds = Array.from(e.touches).map(touch => touch.identifier)

    // Remove touches that are no longer active
    for (let [touchId] of this.touches) {
      if (!activeTouchIds.includes(touchId)) {
        this.touches.delete(touchId)
      }
    }

    const remainingTouches = this.touches.size

    if (remainingTouches === 0) {
      // All touches ended
      this.isRotating = false
      this.isPinching = false

      // Resume auto-rotation after a delay
      setTimeout(() => {
        if (!this.isRotating && !this.isPinching && !this.isDragging) {
          this.autoRotate = true
        }
      }, 2500) // Wait 2.5 seconds before resuming auto-rotation
    }
    else if (remainingTouches === 1 && this.isPinching) {
      // Transitioned from pinch to single touch - switch to rotation
      this.isPinching = false
      this.isRotating = true

      const remainingTouch = Array.from(this.touches.values())[0]
      this.previousTouchPosition = { x: remainingTouch.x, y: remainingTouch.y }
    }
    else if (remainingTouches === 2 && this.isRotating) {
      // Added second touch - switch to pinch zoom
      this.isRotating = false
      this.isPinching = true

      const touchArray = Array.from(this.touches.values())
      const touch1 = touchArray[0]
      const touch2 = touchArray[1]

      this.lastPinchDistance = Math.sqrt(
        Math.pow(touch2.x - touch1.x, 2) + Math.pow(touch2.y - touch1.y, 2),
      )
    }
  }

  /**
   * Handle zoom with mouse wheel
   * Constrains camera distance between 2 and 10 units
   */
  handleWheel = (e) => {
    this.camera.position.z += e.deltaY * 0.01
    this.camera.position.z = Math.max(2, Math.min(this.camera.position.z, 10))
    e.preventDefault()
  }

  /**
   * Handle window resize - update camera and renderer
   */
  handleResize = () => {
    const container = this.containerTarget
    const rect = container.getBoundingClientRect()

    this.camera.aspect = rect.width / rect.height
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(rect.width, rect.height)

    // Recalculate optimal camera position for new container size
    if (this.frame && this.imageLoaded) {
      this.adjustCameraForFrame()
    }
    else {
      // If no image loaded yet, use default dimensions
      this.setInitialCameraPosition()
    }
  }

  // ===== IMAGE HANDLING =====

  /**
   * Update frame dimensions to match uploaded image aspect ratio
   * Maintains consistent frame size while properly scaling for aspect ratio
   */
  updateFrameDimensions(image) {
    if (!image) return

    const imageAspectRatio = image.width / image.height
    const maxFrameWidth = 4 // Maximum frame width
    const maxFrameHeight = 3 // Maximum frame height

    // Calculate frame dimensions that fit within max size while maintaining aspect ratio
    if (imageAspectRatio > 1) {
      // Landscape image
      this.frameWidth = Math.min(maxFrameWidth, maxFrameHeight * imageAspectRatio)
      this.frameHeight = this.frameWidth / imageAspectRatio
    }
    else {
      // Portrait or square image
      this.frameHeight = Math.min(maxFrameHeight, maxFrameWidth / imageAspectRatio)
      this.frameWidth = this.frameHeight * imageAspectRatio
    }

    // Ensure minimum frame size
    const minSize = 1.5
    if (this.frameWidth < minSize) {
      this.frameWidth = minSize
      this.frameHeight = minSize / imageAspectRatio
    }
    if (this.frameHeight < minSize) {
      this.frameHeight = minSize
      this.frameWidth = minSize * imageAspectRatio
    }

    this.rebuildFrame()
    this.imageLoaded = true

    // Adjust camera position for new frame size
    this.adjustCameraForFrame()
  }

  /**
   * Adjust camera position based on current frame dimensions
   */
  adjustCameraForFrame() {
    const fov = this.camera.fov * (Math.PI / 180) // Convert to radians
    const maxDimension = Math.max(this.frameWidth, this.frameHeight)

    // Calculate optimal distance with padding
    const distance = (maxDimension / 2) / Math.tan(fov / 2) * 1.3
    const targetZ = Math.max(distance, 2.5)
    const finalZ = Math.min(targetZ, 8)

    // Smoothly animate to new position
    const startZ = this.camera.position.z
    const duration = 1200 // 1.2 seconds for smooth transition
    const startTime = Date.now()

    const animateCamera = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)

      // Smooth easing function
      const easeProgress = progress * progress * (3 - 2 * progress)

      this.camera.position.z = startZ + (finalZ - startZ) * easeProgress

      if (progress < 1) {
        requestAnimationFrame(animateCamera)
      }
    }

    animateCamera()
  }

  /**
   * Completely rebuild the frame geometry
   * Used when dimensions or colors change
   */
  rebuildFrame() {
    if (this.frame) {
      // Remove from scene
      this.scene.remove(this.frame)

      // Dispose of all geometries and materials for memory cleanup
      this.frame.traverse((object) => {
        if (object.geometry) {
          object.geometry.dispose()
        }
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach((material) => {
              // Don't dispose the photo material as it's reused
              if (material !== this.photoMaterial) {
                if (material.map) material.map.dispose()
                material.dispose()
              }
            })
          }
          else {
            if (object.material !== this.photoMaterial) {
              if (object.material.map) object.material.map.dispose()
              object.material.dispose()
            }
          }
        }
      })
    }

    // Create new frame on next animation frame
    requestAnimationFrame(() => {
      this.frame = this.createFrame()
      this.scene.add(this.frame)
    })
  }

  // ===== ANIMATION =====

  /**
   * Start the render loop
   */
  startAnimationLoop() {
    this.animate()
  }

  /**
   * Main animation loop - renders the scene continuously
   */
  animate() {
    requestAnimationFrame(this.animate.bind(this))

    // Auto-rotate the frame if enabled and not being manually interacted with
    if (this.autoRotate && !this.isDragging && !this.isRotating && !this.isPinching && this.frame) {
      this.frame.rotation.y += this.autoRotateSpeed
    }

    this.renderer.render(this.scene, this.camera)
  }

  // ===== PUBLIC METHODS (called from HTML/Stimulus actions) =====

  /**
   * Change frame to black color
   */
  setBlackFrame() {
    this.changeFrameColor('black')
  }

  /**
   * Change frame to white color
   */
  setWhiteFrame() {
    this.changeFrameColor('white')
  }

  /**
   * Change frame to natural wood color
   */
  setNaturalFrame() {
    this.changeFrameColor('natural')
  }

  /**
   * Reset camera and frame to default position and rotation
   */
  resetView() {
    if (this.frame) {
      // Smoothly animate frame rotation back to zero
      const startRotationX = this.frame.rotation.x
      const startRotationY = this.frame.rotation.y
      const duration = 800 // 0.8 seconds
      const startTime = Date.now()

      const animateReset = () => {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)

        // Smooth easing
        const easeProgress = progress * progress * (3 - 2 * progress)

        this.frame.rotation.x = startRotationX * (1 - easeProgress)
        this.frame.rotation.y = startRotationY * (1 - easeProgress)

        if (progress < 1) {
          requestAnimationFrame(animateReset)
        }
        else {
          // Resume auto-rotation after reset
          this.autoRotate = true
        }
      }

      animateReset()
    }

    // Reset camera to optimal position
    this.adjustCameraForFrame()
  }

  /**
   * Trigger file upload dialog for new image
   */
  uploadImage() {
    // Don't allow file upload if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = this.handleImageUpload.bind(this)
    input.click()
  }

  /**
   * Process uploaded image file
   * Creates texture and updates frame dimensions
   */
  handleImageUpload(e) {
    // Don't allow file upload if src is provided
    if (this.hasSrcValue && this.srcValue) {
      return
    }

    const file = e.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = (event) => {
      // Dispose of previous texture
      if (this.photoTexture.image) {
        this.photoTexture.dispose()
      }

      const image = new Image()
      image.onload = () => {
        // Create new texture from image with proper settings
        const newTexture = new THREE.Texture(image)
        newTexture.needsUpdate = true
        newTexture.colorSpace = THREE.SRGBColorSpace
        newTexture.flipY = true // Flip Y for proper orientation
        newTexture.premultiplyAlpha = false // Don't premultiply alpha
        newTexture.format = THREE.RGBAFormat // Required for sRGB color space
        newTexture.type = THREE.UnsignedByteType // Required for sRGB

        // Update photo material with new texture
        if (this.photoMaterial) {
          this.photoMaterial.map = newTexture
          this.photoMaterial.needsUpdate = true
        }

        this.photoTexture = newTexture
        this.updateFrameDimensions(image)
      }
      image.src = event.target.result
    }
    reader.readAsDataURL(file)
  }

  /**
   * Change the frame color and rebuild
   */
  changeFrameColor(colorName) {
    this.currentFrameColor = this.frameColors[colorName]
    this.frameColorValue = colorName
    this.rebuildFrame()
  }

  // ===== CLEANUP =====

  /**
   * Cleanup when controller disconnects
   * Removes event listeners and disposes of Three.js resources
   */
  disconnect() {
    // Remove mouse event listeners
    this.element.removeEventListener('mousedown', this.handleMouseDown)
    this.element.removeEventListener('mousemove', this.handleMouseMove)
    this.element.removeEventListener('mouseup', this.handleMouseUp)

    // Remove touch event listeners
    this.element.removeEventListener('touchstart', this.handleTouchStart)
    this.element.removeEventListener('touchmove', this.handleTouchMove)
    this.element.removeEventListener('touchend', this.handleTouchEnd)

    // Remove other event listeners
    this.element.removeEventListener('wheel', this.handleWheel)
    window.removeEventListener('resize', this.handleResize)

    // Dispose of textures and materials
    if (this.photoTexture) {
      this.photoTexture.dispose()
    }

    if (this.photoMaterial) {
      this.photoMaterial.dispose()
    }

    // Dispose of all frame geometries and materials
    if (this.frame) {
      this.frame.traverse((object) => {
        if (object.geometry) object.geometry.dispose()
        if (object.material) {
          if (Array.isArray(object.material)) {
            object.material.forEach(material => material.dispose())
          }
          else {
            object.material.dispose()
          }
        }
      })
    }

    // Dispose of renderer
    if (this.renderer) {
      this.renderer.dispose()
    }

    // Clear touch tracking
    if (this.touches) {
      this.touches.clear()
    }
  }
}
